import React from 'react';

function TermsOfUse() {
  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-brand-white mb-2 text-center">
          Terms of Use
        </h1>
        <p className="text-brand-gray-blue text-center mb-8">
          Last Updated: February 2026
        </p>

        <div className="bg-red-900/20 border-2 border-red-500/50 rounded-lg p-6 mb-8">
          <h2 className="text-red-400 font-bold text-lg mb-2">
            Important Disclaimer
          </h2>
          <p className="text-red-300 text-sm leading-relaxed">
            OptionsLeveling is a user-directed computational modeling platform that
            recalculates theoretical option premiums based on defined mathematical models
            and user-selected inputs. The platform does not predict future prices, forecast
            market outcomes, or provide probability estimates of success. All outputs
            represent theoretical recalculations under user-specified assumptions. Nothing
            on this platform constitutes investment advice, financial advice, trading advice,
            or any other sort of advice. OptionsLeveling does not recommend that any
            securities, options, or financial instruments should be bought, sold, or held
            by you. Past performance is not indicative of future results. You are solely
            responsible for your own investment decisions.
          </p>
        </div>

        <p className="text-brand-gray-blue text-sm leading-relaxed mb-8">
          Fooba Labs LLC ("OptionsLeveling," "we," "us," or "our") provides its website and
          platform located at optionsleveling.com (a user-directed computational modeling
          platform for options analytics, together with all associated services, the "Site")
          to you, an individual user ("you"), subject to compliance with the terms and
          conditions set forth herein. Please read these Terms of Use carefully before using
          the Site.
        </p>

        <div className="space-y-6">
          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              1. Agreement to Terms
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              By accessing or using the Site, you agree to be bound by these Terms of Use. If you
              do not agree to these terms, please do not access or use the Site or any information
              contained herein.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              You affirm that you are over the age of 18. The Site is not intended for individuals
              under 18 years of age. If it comes to our attention that a registered user is under
              18, we will cancel that user's account.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              2. Important Securities Disclaimer — Computational Modeling Only
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              OptionsLeveling provides mathematical modeling tools that calculate theoretical
              option values based on current market data and hypothetical scenario inputs
              selected by the user. The platform does not predict future stock prices, future
              option premiums, or future market behavior. All screening outputs, watchlist
              analyses, theoretical premium recalculations, Greeks computations, scenario
              modeling results, dashboard analytics, and other data provided by the Site are
              theoretical outputs derived from computational models under user-specified
              assumptions. They are provided for informational and educational purposes only.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              You further understand that OptionsLeveling, Fooba Labs LLC, and their affiliates
              are not advising you personally concerning the nature, potential, value, or
              suitability of any particular security, portfolio of securities, transaction,
              investment strategy, or other matter.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              Options trading involves substantial risk of loss, including the potential loss of
              your entire investment. Certain options strategies, including but not limited to
              uncovered (naked) options writing, carry risk of losses that may exceed your initial
              investment. The Site does not perform any suitability assessment of your financial
              situation, investment experience, or risk tolerance. You acknowledge that you
              understand the risks of derivatives trading and accept full responsibility for your
              trading decisions.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              You understand that market data and computational inputs are supplied by third-party
              sources believed to be reliable, that the theoretical recalculations on our Site are
              made using such data, and that such recalculations are not guaranteed by these
              sources, the information providers, or any other person or entity, and may not be
              complete or accurate.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              All content on the Site is presented only as of the date published or indicated,
              and may be superseded by subsequent market events or for other reasons. You are
              responsible for verifying the timeliness and accuracy of any data before making
              investment decisions.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              3. No Personal Financial Advice; No Fiduciary Duty
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              The content on the Site is provided for informational purposes only. The Site does
              not provide tax, legal, insurance, or investment advice, and nothing on the Site
              should be construed as an offer to sell, a solicitation of an offer to buy, or a
              recommendation for any security by OptionsLeveling or any third party.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              OptionsLeveling does not know your financial situation, risk tolerance, or
              investment objectives. Any screening outputs, watchlist analyses, theoretical
              premium recalculations, or dashboard analytics are model-derived values generated
              under user-specified parameters, not personalized recommendations. You alone are
              solely responsible for determining whether any investment, security, or strategy
              is appropriate for you based on your own circumstances.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              You should consult a licensed financial advisor, attorney, or tax professional
              regarding your specific situation before making any investment decisions based on
              information obtained from the Site.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              OptionsLeveling is not registered with the U.S. Securities and Exchange Commission
              (SEC), the Financial Industry Regulatory Authority (FINRA), the Commodity Futures
              Trading Commission (CFTC), or any other federal, state, or foreign regulatory
              authority. OptionsLeveling does not provide brokerage, advisory, or investment
              banking services. No fiduciary relationship, duty, or obligation is created between
              OptionsLeveling and any user by virtue of your access to or use of the Site. Your
              use of the Site is at your own risk and discretion.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              4. Model Limitations and Assumptions
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              The Site employs the Black-Scholes option pricing model and related computational
              methods to generate theoretical premium values. These models rely on simplifying
              assumptions, including but not limited to: constant volatility over the life of the
              option, continuous and frictionless markets, log-normal distribution of underlying
              asset returns, constant risk-free interest rates, and no dividends (or known
              discrete dividends).
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              Actual market conditions may differ materially from these assumptions. Volatility
              is not constant, markets experience gaps and discontinuities, and asset returns may
              exhibit skewness and excess kurtosis not captured by log-normal models. As a result,
              model-derived values may diverge significantly from actual market prices.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              All outputs generated by the Site — including theoretical premiums, Greeks values,
              and scenario modeling results — are mathematical recalculations, not guarantees or
              predictions of future market behavior. You should not rely on any model output as
              the sole basis for any investment decision.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              5. Data Sources and Third-Party Providers
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              The Site sources market data, options chain information, and related financial data
              from third-party providers, including but not limited to Polygon.io and EODHD.
              OptionsLeveling does not independently verify the accuracy, completeness, or
              timeliness of data received from these providers.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              Third-party data may be delayed, inaccurate, incomplete, or subject to interruption
              or discontinuation without notice. Data providers may modify their services,
              coverage, or terms at any time, which may require OptionsLeveling to modify, limit,
              or discontinue certain features of the Site.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              OptionsLeveling is not liable for any losses, damages, or claims arising from data
              interruptions, inaccuracies, or discontinuations by third-party providers. You
              acknowledge that the availability and accuracy of data on the Site depend on factors
              outside of our reasonable control.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              6. Account Rules
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              In order to access some of the services of the Site, you will have to create an
              account. By creating an account, you agree to the following:
            </p>
            <ul className="text-brand-gray-blue text-sm leading-relaxed space-y-2 list-disc list-inside">
              <li>You may only maintain a single account.</li>
              <li>You may never share your account credentials or knowingly provide or authorize access to your account.</li>
              <li>You may never use another user's account without permission.</li>
              <li>When creating your account, you must provide accurate and complete information.</li>
              <li>You are solely responsible for the activity that occurs on your account, and you must keep your account password secure.</li>
              <li>You must notify us immediately of any breach of security or unauthorized use of your account.</li>
            </ul>
            <p className="text-brand-gray-blue text-sm leading-relaxed mt-3">
              You will be liable for any use made of your account or password and the losses of
              OptionsLeveling or others due to such unauthorized use. We will not be liable for
              your losses caused by any unauthorized use of your account.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              7. User Conduct
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              In connection with your use of the Site and its services, you agree not to:
            </p>
            <ul className="text-brand-gray-blue text-sm leading-relaxed space-y-2 list-disc list-inside">
              <li>Restrict or inhibit any other user from using the Site, including by means of "hacking" or defacing any portion of the Site.</li>
              <li>Use the Site for any unlawful purpose.</li>
              <li>Modify, adapt, sublicense, translate, sell, reverse engineer, decompile, or disassemble any portion of the Site.</li>
              <li>Use any robot, spider, scraper, or other automated device or process to access, retrieve, index, or "data mine" the Site or its contents.</li>
              <li>Redistribute, share, or resell data or analytics obtained through paid subscription tiers in bulk or for commercial purposes.</li>
              <li>Transmit any content which contains software viruses or other harmful computer code, files, or programs.</li>
              <li>Interfere with or disrupt the Site or the servers or networks providing the Site.</li>
            </ul>
            <p className="text-brand-gray-blue text-sm leading-relaxed mt-3">
              You also agree to comply with all applicable laws, rules, and regulations in
              connection with your use of the Site.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              8. Subscriptions and Payments
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              OptionsLeveling offers both free and paid subscription tiers. By subscribing to a
              paid plan, you agree to pay the applicable subscription fees as set forth on the
              Site. All payments are processed securely through Stripe.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              By subscribing to a paid plan, you expressly authorize OptionsLeveling to charge
              your designated payment method on a recurring basis at the applicable subscription
              rate until you cancel. Unless stated otherwise, subscription fees are billed on a
              monthly basis and are nonrefundable. No refunds will be issued for partial billing
              periods, unused features, or early cancellation. You may cancel your subscription
              at any time, and cancellation will take effect at the end of the current billing
              period.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              OptionsLeveling reserves the right to revise subscription fees upon reasonable
              notice. Free tier usage limits apply as described on the pricing page.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              OptionsLeveling reserves the right to cancel a subscription at any time. If we
              cancel a subscription due to a breach of these Terms of Use, you will not be
              eligible for any refund.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              9. Intellectual Property
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              The Site and the content contained herein, including without limitation all text,
              software, graphics, analytics algorithms, user interfaces, and the trademarks,
              service marks, and logos contained therein, are the property of Fooba Labs LLC and
              its third-party licensors or providers.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              You may access and use the Site solely for your personal, non-commercial use. You
              may not copy, modify, distribute, or create derivative works from any content on
              the Site without prior written permission from OptionsLeveling.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              Market data displayed on the Site is sourced from third-party providers including
              EODHD and Polygon.io. Such data remains the property of the respective providers
              and is subject to their terms of use.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              10. Disclaimer of Warranties
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3 uppercase font-semibold">
              The Site, and any product or service obtained or accessed through the Site, is
              provided "as is" and "as available" without representations or warranties of any
              kind, either express or implied. To the fullest extent permitted by applicable law,
              OptionsLeveling, Fooba Labs LLC, its officers, directors, employees, affiliates,
              suppliers, and information providers disclaim all warranties, express, implied, or
              statutory, including but not limited to warranties of title, non-infringement,
              merchantability, and fitness for a particular purpose.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              As described in Section 4, the computational models employed by the Site rely on
              theoretical assumptions that may not reflect actual market conditions. Model-derived
              values, including theoretical premiums, Greeks, and scenario outputs, are not
              guaranteed to be accurate, complete, or reflective of real-world option pricing.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              We do not warrant that your use of the Site will be uninterrupted, error-free, or
              secure, or that the Site or the servers on which the Site is hosted are free of
              viruses or other harmful components. You assume total responsibility and risk for
              your use of the Site and your reliance thereon.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              11. Force Majeure
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              OptionsLeveling shall not be liable for any failure or delay in performing its
              obligations under these Terms to the extent that such failure or delay results from
              events beyond our reasonable control, including but not limited to: market shutdowns
              or trading halts imposed by exchanges or regulatory authorities; data provider
              outages, interruptions, or discontinuations; internet or telecommunications
              failures; natural disasters; acts of war, terrorism, or civil unrest; government
              actions, orders, or sanctions; cyberattacks or security breaches; pandemics or
              public health emergencies; and any other force majeure events. During any such
              event, our obligations under these Terms shall be suspended for the duration of the
              event.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              12. Limitation of Liability
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3 uppercase font-semibold">
              Neither OptionsLeveling, Fooba Labs LLC, nor their respective officers, directors,
              employees, affiliates, suppliers, or information providers are liable for lost
              profits, lost savings, trading losses, missed investment opportunities, losses
              arising from reliance on scenario modeling outputs, theoretical premium
              recalculations, screener rankings or results, watchlist analyses, Greeks
              computations, or any other model-derived value provided by the Site, or any
              indirect, incidental, consequential, special, exemplary, punitive, or other damages
              under any contract, negligence, strict liability, or other theory arising out of or
              relating in any way to the Site and/or any content contained therein.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              This includes, without limitation, any losses arising from decisions made based on
              screener outputs, watchlist analyses, theoretical premium recalculations, scenario
              modeling results, Greeks computations, dashboard analytics, or any other data or
              model-derived output from the Site. All such outputs are theoretical in nature and
              are calculated under user-specified assumptions.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              Your sole remedy for dissatisfaction with the Site is to stop using it. The sole
              and exclusive maximum liability to OptionsLeveling for all damages, losses, and
              causes of action shall not exceed the total amount paid to us by you, if any, for
              access to the Site during the previous twelve (12) months prior to bringing the
              claim.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              13. Indemnification
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              As a condition of your use of the Site, you agree to indemnify, defend, and hold
              harmless Fooba Labs LLC, OptionsLeveling, and their officers, directors, employees,
              agents, and representatives from and against any and all claims, damages, losses,
              costs (including reasonable attorneys' fees), or other expenses that arise directly
              or indirectly out of or from (a) your violation of these Terms of Use; (b) your use
              of, or inability to use, the Site; (c) your violation of the rights of any third
              party; or (d) any investment decisions you make based on information obtained from
              the Site.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              14. Termination
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              You understand and agree that OptionsLeveling may, under certain circumstances and
              without prior notice, terminate your access to and use of the Site. Cause for such
              termination shall include, but not be limited to: (i) breaches or violations of
              these Terms of Use, (ii) requests by law enforcement or other government or
              regulatory authorities, or (iii) unexpected technical or security issues.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              You may delete your account at any time. Upon termination, your saved contracts,
              screener parameters, watchlist groups, dashboard configurations, and other user
              data may be permanently deleted.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              15. Changes to Terms
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              We reserve the right to modify these Terms of Use at any time. We may also change
              the Site, including eliminating or discontinuing any content or feature, or impose
              fees, charges, or other conditions with reasonable notice. Your continued use of the
              Site following any changes constitutes your acceptance of the revised terms. You
              should review these Terms of Use periodically for changes.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              16. Dispute Resolution — Binding Arbitration
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3 uppercase font-semibold">
              You and OptionsLeveling agree that any dispute, claim, or controversy arising out
              of or relating to these Terms of Use or the use of the Site shall be resolved
              exclusively through final and binding arbitration, rather than in court, except
              that either party may seek injunctive or equitable relief in court for claims
              related to intellectual property.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              Arbitration shall be conducted in Miami-Dade County, Florida under the rules of
              the American Arbitration Association (AAA) then in effect. The arbitration shall be
              conducted by a single arbitrator, and the arbitrator's decision shall be final and
              binding. Judgment on the arbitration award may be entered in any court of competent
              jurisdiction.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              By agreeing to these Terms, you waive your right to a jury trial and your right to
              participate in a court proceeding with respect to any dispute covered by this
              arbitration provision.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              If for any reason this arbitration provision is found to be unenforceable, the
              dispute resolution provisions in Section 18 (Governing Law and Venue) shall apply.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              17. Class Action Waiver
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3 uppercase font-semibold">
              You agree that any claims arising out of or relating to these Terms of Use or
              the use of the Site shall be brought solely in your individual capacity and not
              as a plaintiff or class member in any purported class, collective, consolidated,
              or representative proceeding.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              You expressly waive any right to participate in a class action lawsuit or
              class-wide arbitration against OptionsLeveling, Fooba Labs LLC, or any of their
              affiliates, officers, directors, or employees.
            </p>
          </section>

          <section className="bg-surface border border-muted rounded-lg p-6">
            <h2 className="text-xl font-semibold text-brand-white mb-3">
              18. Governing Law and Venue
            </h2>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              These Terms of Use and the relationship between you and OptionsLeveling are governed
              by and construed in accordance with the laws of the State of Florida, without regard
              to its principles of conflict of laws.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              To the extent any dispute is not subject to the binding arbitration provision in
              Section 16, you agree that such dispute shall be brought exclusively in the state
              or federal courts located in Miami-Dade County, Florida. You consent to the personal
              jurisdiction of and venue in such courts and waive any objection based on
              inconvenient forum.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed mb-3">
              If the arbitration provision in Section 16 is found to be unenforceable with respect
              to a particular claim or dispute, you agree that such claim or dispute shall be
              resolved exclusively in the state or federal courts located in Miami-Dade County,
              Florida.
            </p>
            <p className="text-brand-gray-blue text-sm leading-relaxed">
              If any provision of these Terms of Use is found to be unlawful, void, or for any
              reason unenforceable, then that provision shall be deemed severable and shall not
              affect the validity and enforceability of any remaining provisions.
            </p>
          </section>
        </div>

        <div className="text-center mt-8 space-y-2">
          <p className="text-brand-gray-blue text-xs">
            &copy; {new Date().getFullYear()} Fooba Labs LLC. All rights reserved.
          </p>
          <div className="flex justify-center gap-4">
            <a
              href="/privacy"
              className="text-brand-blue hover:text-[#1a7aef] text-sm transition-colors"
            >
              Privacy Policy
            </a>
            <a
              href="/login"
              className="text-brand-blue hover:text-[#1a7aef] text-sm transition-colors"
            >
              Back to Login
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsOfUse;
